import {ChevronDownIcon, ChevronUpIcon, Divider} from 'native-base';
import {OrderItemStatus, OrderItems} from '@buncha/batchDetails/types';
import React, {useCallback, useEffect, useState} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Accordian} from '@buncha/components/composites';
import {Analytics} from '@buncha/config/analytics/analytics';
import AppModal from '@buncha/common/components/CustomModal/AppModal';
import {B2BOrderInfoParam} from '../types';
import {B2BScreens} from '../navigation/types';
import BatchDetailHeader from '@buncha/batchDetails/component/BatchDetailHeader';
import {Box} from '@buncha/components/core/view';
import {ClientType} from '@buncha/common/types/common';
import {CustomFlatList} from '@buncha/components/core/list';
import {CustomText} from '@buncha/components/core/text';
import {EventType} from '@buncha/config/analytics/type';
import Header from '@buncha/components/composites/Header/Header';
import {Modules} from '@buncha/utils/types';
import ReturnOrderItem from '../components/ReturnOrderItem';
import ScreenHeader from '@buncha/common/components/screenHeader';
import {StyleSheet} from 'react-native';
import UserHeader from '../components/UserHeader';
import {useDropOrderItem} from '@buncha/itemDetails/hooks/ItemDetails';
import {useGetLocale} from '@buncha/localization/hooks';
import {useGetOrderInfoDetails} from '../hooks/B2BChekout';
import {useGetRunForBatchDetail} from '@buncha/batchDetails/hooks/BatchDetails';
import {useRoute} from '@react-navigation/native';

const B2BOrderInfo: React.FC = () => {
  const theme = useColorThemeType();
  const muliFont = useFontTheme('muli');
  const routeParams = useRoute()?.params as B2BOrderInfoParam | undefined;
  const runId = Number(routeParams?.runId) || 0;
  const orderId = Number(routeParams?.orderId) || 0;
  const screenTitle = routeParams?.screenTitle;
  const {body} = useGetLocale(Modules.B2B, B2BScreens.B2BOrderInfo) ?? {};
  const [filterItem, setFilterItem] = useState<OrderItems[] | undefined>([]);
  const [modal, dropModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OrderItems>();
  const {dropOrderItem, droppingItem} = useDropOrderItem();

  const [getRunForBatchDetail, runState] = useGetRunForBatchDetail(null);
  const {getOrderInfoDetails, loading, orderInfoData} =
    useGetOrderInfoDetails();

  const toggleModal = useCallback(() => {
    if (droppingItem) return;
    dropModal(!modal);
  }, [droppingItem, modal]);

  const onDrop = useCallback(async () => {
    Analytics.eventWithProps('item drop', {
      type: EventType.cta,
      runId: runId,
      client: ClientType.meijer,
      name: selectedItem?.name,
      category: selectedItem?.tags,
      legacyId: orderId,
    });
    const res = await dropOrderItem(runId, [selectedItem?.id ?? 0]);
    if (res?.id) {
      const dropFilterItem = filterItem?.filter(
        orderItem => orderItem?.id !== selectedItem?.id,
      );
      setFilterItem(dropFilterItem);
    }
    toggleModal();
  }, [
    dropOrderItem,
    filterItem,
    orderId,
    runId,
    selectedItem?.id,
    selectedItem?.name,
    selectedItem?.tags,
    toggleModal,
  ]);

  const onClick = useCallback(
    (item: OrderItems) => {
      setSelectedItem(item);
      toggleModal();
    },
    [toggleModal],
  );

  const buyerFullName =
    (orderInfoData?.buyer?.firstName ?? '') +
    ' ' +
    (orderInfoData?.buyer?.lastName ?? '');

  useEffect(() => {
    if (runId) getRunForBatchDetail(runId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  useEffect(() => {
    if (orderInfoData?.items.length) {
      const allItems = orderInfoData?.items?.filter(
        orderItem => orderItem?.itemStatus === OrderItemStatus.packed,
      );
      setFilterItem(allItems);
    }
  }, [orderInfoData?.items]);

  useEffect(() => {
    if (orderId && runId) getOrderInfoDetails(`${orderId}`, runId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, runId]);
  const renderItem = useCallback(
    ({item}: {item: OrderItems}) => {
      return <ReturnOrderItem item={item} onDrop={onClick} />;
    },
    [onClick],
  );

  const divider = useCallback(
    () => <Divider style={styles.bottomDivider} />,
    [],
  );

  const renderMain = useCallback(() => {
    return (
      <Accordian
        isExpanded={true}
        containerStyle={[styles.listStyle]}
        childContainerStyle={[styles.flex, styles.listContainer]}
        header={
          <UserHeader
            buyerName={buyerFullName}
            subText={body?.itemsPicked?.replace(
              '{filterItems}',
              filterItem?.length,
            )}
            leftComponent={
              <Box
                style={[
                  styles.countBox,
                  {
                    backgroundColor: theme?.background.gray[200],
                  },
                ]}>
                <CustomText
                  style={[
                    {
                      fontFamily: muliFont[700].normal,
                      color: theme?.text.gray[100],
                    },
                  ]}>
                  {filterItem?.length}
                </CustomText>
              </Box>
            }
          />
        }
        iconEnable={<ChevronUpIcon color={theme?.text.blue[300]} />}
        iconDisable={<ChevronDownIcon color={theme?.text.blue[300]} />}>
        <CustomFlatList
          style={[styles.flex]}
          contentContainerStyle={[styles.flex]}
          data={filterItem}
          renderItem={renderItem}
          ItemSeparatorComponent={divider}
        />
      </Accordian>
    );
  }, [
    body?.itemsPicked,
    buyerFullName,
    divider,
    filterItem,
    muliFont,
    renderItem,
    theme?.background.gray,
    theme?.text.blue,
    theme?.text.gray,
  ]);

  return (
    <Header loading={runState?.loading || loading} showDefaultLeftHeader>
      <ScreenHeader title={screenTitle ?? ''} />
      <BatchDetailHeader
        showRunPill={true}
        runDetail={runState?.data ?? undefined}
        upcOrderData={undefined}
        hidSubText={true}
      />
      <Divider style={styles.dividerStyle} />
      <Box style={styles.flex}>
        <CustomFlatList
          ListHeaderComponent={renderMain()}
          showsVerticalScrollIndicator={false}
          data={undefined}
          renderItem={undefined}
        />
      </Box>
      <AppModal
        isOpen={modal}
        onClose={toggleModal}
        topCTAHandelr={onDrop}
        title={'Drop this item'}
        subTitle="This will remove this item from this order. Are you sure?"
        ctaBottomTitle={'No'}
        hideBottomCta={droppingItem}
        ctaTopTitle={'Yes'}
        topCTAColor={theme?.background.red[100]}
        bottomCTAHandelr={toggleModal}
        disableCtaTop={droppingItem}
        loading={droppingItem}
      />
    </Header>
  );
};

export default React.memo(B2BOrderInfo);

const styles = StyleSheet.create({
  subContainer: {justifyContent: 'flex-end'},
  countBox: {
    paddingHorizontal: 15,
    borderRadius: 50,
    paddingVertical: 2.5,
    marginRight: 10,
  },
  flex: {flex: 1},
  dividerStyle: {marginBottom: 15},
  listStyle: {marginHorizontal: 20},
  bottomDivider: {marginTop: 10, marginBottom: 10},
  listContainer: {marginTop: 10},
});
